// TwinAnimatedImage.tsx
import { useEffectPlease } from "@features/utils";
import { useRef } from "react";

type Props = {
  src: string;
  layoutId: string;
  className?: string;
  style?: React.CSSProperties;
  divRef?: React.RefObject<HTMLDivElement>;
  id?: string;
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  crossOrigin?: "anonymous" | "use-credentials";
  onMouseEnter?: (event: React.MouseEvent<HTMLImageElement>) => void;
  onMouseLeave?: () => void;
  onMouseMove?: (event: React.MouseEvent<HTMLImageElement>) => void;
};

export const TwinAnimatedImage = ({
  src,
  layoutId,
  className,
  style,
  id,
  onLoad,
  crossOrigin,
  ...props
}: Props) => {
  const ref = useRef<HTMLImageElement>(null);
  const cachedBoundingRect = useRef<DOMRect | null>(null);

  const handleClick = () => {
    if (ref.current) {
      cachedBoundingRect.current = ref.current.getBoundingClientRect();
    }
  };

  useEffectPlease(() => {
    return async () => {
      // Detect if a new version of the image exists somewhere when this one is destroyed
      let candidate = document.querySelector(
        `#${layoutId}`
      ) as HTMLImageElement;
      if (candidate) {
        const selfPositionOnScreen = cachedBoundingRect.current;
        const candidatePositionOnScreen = candidate.getBoundingClientRect();
        // Take new image, put it at the same position on screen as the old one and transition it
        if (selfPositionOnScreen && candidatePositionOnScreen) {
          const realTargetCandidate = candidate;
          realTargetCandidate.style.opacity = "0"; // Fade out the old image

          // First copy the candidate so we can work with a floating element
          candidate = candidate.cloneNode(true) as HTMLImageElement;
          candidate.style.position = "fixed";
          candidate.style.left = `${selfPositionOnScreen.left}px`;
          candidate.style.top = `${selfPositionOnScreen.top}px`;
          candidate.style.width = `${selfPositionOnScreen.width}px`;
          candidate.style.height = `${selfPositionOnScreen.height}px`;
          candidate.style.zIndex = "1000"; // Ensure it is above other content
          candidate.style.transition = "all 0.2s ease"; // Smooth transition
          candidate.style.opacity = "1"; // Ensure it is visible
          document.body.appendChild(candidate);
          requestAnimationFrame(() => {
            candidate.style.left = `${candidatePositionOnScreen.left}px`;
            candidate.style.top = `${candidatePositionOnScreen.top}px`;
            candidate.style.width = `${candidatePositionOnScreen.width}px`;
            candidate.style.height = `${candidatePositionOnScreen.height}px`;
            setTimeout(() => {
              realTargetCandidate.style.opacity = "1"; // Fade out the old image
              candidate.remove(); // Remove the floating element after transition
            }, 300); // Match the transition duration
          }); // Match the transition duration
        }
      }
    };
  }, []);

  if (src?.includes(".mp4") || src?.includes(".webm")) {
    return (
      <video
        id={layoutId}
        src={src}
        className={"w-full h-full object-cover"}
        autoPlay
        muted
        loop
        playsInline
      />
    );
  }

  return (
    <img
      onLoad={(e) => {
        cachedBoundingRect.current = e.currentTarget.getBoundingClientRect();
        onLoad?.(e);
      }}
      id={layoutId}
      ref={ref}
      src={src}
      className={className}
      alt={id}
      crossOrigin={crossOrigin}
      style={{ ...style }}
      onClick={handleClick}
      {...props}
    />
  );
};
